import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';

const ITYPES = ['afrekening', 'tussentijds', 'voorschot', 'jaarafrekening'];
const GROUPS = ['afname', 'injectie', 'groene_stroom', 'wkk', 'netwerk', 'toeslagen', 'andere'];

function num(v) { return v === '' || v == null ? '' : v; }

/**
 * Herbruikbaar factuurformulier met detail-lijnopbouw.
 * props:
 *   connections, initial, invoiceId, meta, onSaved
 */
export default function InvoiceForm({ connections, initial = {}, invoiceId = null, meta = {}, onSaved = null }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    connection_id:     initial.connection_id     || (connections[0]?.id ?? ''),
    invoice_number:    initial.invoice_number    || '',
    invoice_type:      initial.invoice_type      || 'afrekening',
    invoice_date:      (initial.invoice_date     || '').slice(0, 10),
    period_start:      (initial.period_start      || '').slice(0, 10),
    period_end:        (initial.period_end        || '').slice(0, 10),
    consumption_total: num(initial.consumption_total),
    consumption_kwh:   num(initial.consumption_kwh),
    conversion_factor: num(initial.conversion_factor),
    consumption_day:   num(initial.consumption_day),
    consumption_night: num(initial.consumption_night),
    injection_kwh:     num(initial.injection_kwh),
    peak_power_kw:     num(initial.peak_power_kw),
    amount_excl_vat:   num(initial.amount_excl_vat),
    vat_amount:        num(initial.vat_amount),
    amount_incl_vat:   num(initial.amount_incl_vat),
    notes:             initial.notes || '',
  });

  const [lines, setLines] = useState(
    (initial.lines || []).map(l => ({
      line_group: l.line_group || 'andere',
      label:      l.label || '',
      quantity:   num(l.quantity),
      unit_price: num(l.unit_price),
      amount:     num(l.amount),
    }))
  );

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }
  function setLine(i, field, val) { setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [field]: val } : l)); }
  function addLine() { setLines(ls => [...ls, { line_group: 'andere', label: '', quantity: '', unit_price: '', amount: '' }]); }
  function removeLine(i) { setLines(ls => ls.filter((_, idx) => idx !== i)); }

  const selectedConn = connections.find(c => String(c.id) === String(form.connection_id));
  const isElec = selectedConn?.energy_type === 'elektriciteit';
  const hasLines = lines.length > 0;

  const exclDerived = Math.round(lines.reduce((a, l) => a + (parseFloat(l.amount) || 0), 0) * 100) / 100;
  const excl = hasLines ? exclDerived : (parseFloat(form.amount_excl_vat) || null);
  const vat  = parseFloat(form.vat_amount);
  const incl = (excl != null && !Number.isNaN(vat)) ? Math.round((excl + vat) * 100) / 100
             : (parseFloat(form.amount_incl_vat) || null);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    const cleanLines = lines
      .filter(l => l.label || l.amount !== '')
      .map(l => ({
        line_group: l.line_group,
        label:      l.label,
        quantity:   l.quantity === '' ? null : Number(l.quantity),
        unit_price: l.unit_price === '' ? null : Number(l.unit_price),
        amount:     l.amount === '' ? null : Number(l.amount),
      }));
    const payload = {
      ...form,
      amount_excl_vat: excl,
      amount_incl_vat: incl,
      lines: cleanLines,
      source:       meta.source       || initial.source       || 'manual',
      parse_status: meta.parse_status || initial.parse_status || 'manual',
      pdf_filename: meta.pdf_filename  || initial.pdf_filename || null,
      raw_json:     meta.raw_json,
    };
    try {
      if (invoiceId) await api.put(`/api/invoices/${invoiceId}`, payload);
      else           await api.post('/api/invoices', payload);
      if (onSaved) onSaved();
      else         navigate('/invoices');
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setLoading(false);
    }
  }

  const numField = (key, label) => (
    <div className="col-md-4 mb-3">
      <label className="form-label small fw-medium">{label}</label>
      <input type="number" step="any" className="form-control" value={form[key]}
        onChange={e => set(key, e.target.value)} />
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-danger py-2">{error}</div>}

      {/* Kop */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-0">
            <div className="col-md-4 mb-3 pe-md-3">
              <label className="form-label small fw-medium">{t('inv_connection')} *</label>
              <select className="form-select" value={form.connection_id}
                onChange={e => set('connection_id', e.target.value)} required>
                {connections.map(c => (
                  <option key={c.id} value={c.id}>{c.label} ({t(`type_${c.energy_type}`)})</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 mb-3 pe-md-3">
              <label className="form-label small fw-medium">{t('inv_number')}</label>
              <input className="form-control" value={form.invoice_number}
                onChange={e => set('invoice_number', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label small fw-medium">{t('inv_type')}</label>
              <select className="form-select" value={form.invoice_type}
                onChange={e => set('invoice_type', e.target.value)}>
                {ITYPES.map(it => <option key={it} value={it}>{t(`itype_${it}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="row g-0">
            <div className="col-md-4 mb-3 pe-md-3">
              <label className="form-label small fw-medium">{t('inv_date')}</label>
              <input type="date" className="form-control" value={form.invoice_date}
                onChange={e => set('invoice_date', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3 pe-md-3">
              <label className="form-label small fw-medium">{t('inv_period_start')}</label>
              <input type="date" className="form-control" value={form.period_start}
                onChange={e => set('period_start', e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label small fw-medium">{t('inv_period_end')}</label>
              <input type="date" className="form-control" value={form.period_end}
                onChange={e => set('period_end', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Verbruik */}
      <div className="card mb-3">
        <div className="card-header bg-white fw-medium" style={{ fontSize: 14 }}>{t('inv_consumption')}</div>
        <div className="card-body">
          <div className="row g-0">
            {isElec ? (
              <>
                {numField('consumption_total', t('f_consumption_total'))}
                {numField('consumption_day', t('f_consumption_day'))}
                {numField('consumption_night', t('f_consumption_night'))}
                {numField('peak_power_kw', t('f_peak_power'))}
                {selectedConn?.has_solar && numField('injection_kwh', t('f_injection'))}
              </>
            ) : (
              <>
                {numField('consumption_total', t('f_gas_m3'))}
                {numField('consumption_kwh', t('f_gas_kwh'))}
                {numField('conversion_factor', t('f_factor'))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detailopbouw */}
      <div className="card mb-3">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <span className="fw-medium" style={{ fontSize: 14 }}>{t('lines_title')}</span>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={addLine}>
            <i className="bi bi-plus me-1" />{t('line_add')}
          </button>
        </div>
        <div className="card-body">
          <div className="text-muted-sm mb-2">{t('lines_hint')}</div>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-2">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 150 }}>{t('col_group')}</th>
                  <th>{t('col_desc')}</th>
                  <th style={{ width: 110 }} className="text-end">{t('col_qty')}</th>
                  <th style={{ width: 130 }} className="text-end">{t('col_price')}</th>
                  <th style={{ width: 120 }} className="text-end">{t('col_amount')}</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <select className="form-select form-select-sm" value={l.line_group}
                        onChange={e => setLine(i, 'line_group', e.target.value)}>
                        {GROUPS.map(g => <option key={g} value={g}>{t(`group_${g}`)}</option>)}
                      </select>
                    </td>
                    <td><input className="form-control form-control-sm" value={l.label}
                      onChange={e => setLine(i, 'label', e.target.value)} /></td>
                    <td><input type="number" step="any" className="form-control form-control-sm text-end"
                      value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} /></td>
                    <td><input type="number" step="any" className="form-control form-control-sm text-end"
                      value={l.unit_price} onChange={e => setLine(i, 'unit_price', e.target.value)} /></td>
                    <td><input type="number" step="any" className="form-control form-control-sm text-end"
                      value={l.amount} onChange={e => setLine(i, 'amount', e.target.value)} /></td>
                    <td className="text-center">
                      <button type="button" className="btn btn-sm btn-link text-danger p-0"
                        onClick={() => removeLine(i)}><i className="bi bi-x-lg" /></button>
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr><td colSpan="6" className="text-center text-muted py-2">—</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totalen */}
          <div className="row g-0 justify-content-end">
            <div className="col-md-4 mb-2">
              <label className="form-label small fw-medium">{t('f_amount_excl')}</label>
              {hasLines ? (
                <input className="form-control bg-light" value={exclDerived} readOnly />
              ) : (
                <input type="number" step="any" className="form-control" value={form.amount_excl_vat}
                  onChange={e => set('amount_excl_vat', e.target.value)} />
              )}
            </div>
            <div className="col-md-4 mb-2 px-md-3">
              <label className="form-label small fw-medium">{t('f_vat')}</label>
              <input type="number" step="any" className="form-control" value={form.vat_amount}
                onChange={e => set('vat_amount', e.target.value)} />
            </div>
            <div className="col-md-4 mb-2">
              <label className="form-label small fw-medium">{t('f_amount_incl')}</label>
              <input className="form-control bg-light fw-medium"
                value={incl ?? ''} readOnly />
            </div>
          </div>

          <div className="mt-2">
            <label className="form-label small fw-medium">{t('f_notes')}</label>
            <textarea className="form-control" rows="2" value={form.notes}
              onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/invoices')}>
          {t('cancel')}
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading || !form.connection_id}>
          {loading && <span className="spinner-border spinner-border-sm me-2" />}
          {t('save_invoice')}
        </button>
      </div>
    </form>
  );
}
